import { useCallback, useEffect, useState } from 'react';
import { isNull } from 'lodash-es';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { getAllCards } from '../../api/bankCard';
import { Card } from '../../api/bankCard/types';
import { PAN_TYPE } from '../../constants';
import { ApiError } from '../../types/apiError';
import { findCardStatus, withdrawAllowed } from '../../utils';

type FindCardParams = {
  cardNumber?: string;
  cvv?: string;
};

export const useBCList = (userId: number, isWithdraw?: boolean) => {
  const [cardList, setCardList] = useState<Card[] | null>(null);
  const [isCardsLoading, setIsCardsLoading] = useState(false);

  const findCard = useCallback(
    (card: FindCardParams) =>
      cardList?.find(({ cardHash, cardNumber }) =>
        cardHash
          ? cardHash === card?.cardNumber
          : cardNumber === card?.cardNumber
      ),
    [cardList]
  );

  const fetchCards = useCallback(async () => {
    let cards: Card[] = [];
    try {
      setIsCardsLoading(true);
      const { response = [] } = await getAllCards({
        userId
      });
      const processedResponse = response.map((item) => {
        const { type: cardType, status, cardStatus } = item;
        let processedItem = {
          ...item,
          hasNtf: status.docRequested
        };
        if (cardType === PAN_TYPE.WALLET && !cardStatus) {
          processedItem = {
            ...processedItem,
            cardStatus: findCardStatus(status)
          };
        }
        return processedItem;
      });
      cards = isWithdraw
        ? processedResponse.filter(({ cardStatus }) =>
            withdrawAllowed(cardStatus)
          )
        : processedResponse;
    } catch (e) {
      const { error = {} } = e as ApiError;
      logAction(LOG_ACTIONS.PANKEEPER_GET_ALL_PANS_FAILED, {
        error
      });
      console.error(error);
    } finally {
      setIsCardsLoading(false);
    }
    return setCardList(cards);
  }, [isWithdraw, userId]);

  useEffect(() => {
    if (isNull(cardList) && !isCardsLoading) {
      fetchCards();
    }
  }, [cardList, fetchCards, isCardsLoading]);

  return {
    cardList,
    findCard,
    fetchCards,
    setCardList,
    isCardsLoaded: !isNull(cardList) && !isCardsLoading
  };
};
